import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { AlertTriangle, Shield, Server, Target, FolderOpen, Bug, CheckCircle, XCircle, ChevronRight, Brain, Loader2, RefreshCw, X, Lightbulb, AlertOctagon, Crosshair, FileText, ArrowUpRight } from 'lucide-react';
import { useI18n } from '../i18n';
import ReactMarkdown from 'react-markdown';
import MarkdownRenderer from './common/MarkdownRenderer';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Helper function to validate UUID format
const isValidUUID = (str) => {
  if (!str || typeof str !== 'string') return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

/**
 * SankeyWorkspaceFlow - Diagramme Sankey pour visualiser:
 * Workspaces -> Targets -> Assets -> Vulnerabilities -> (Resolved / Unresolved)
 *
 * With detailed AI analysis panel on click for each element
 */
const SankeyWorkspaceFlow = ({ scans = [] }) => {
  console.log('[Sankey] Component mounted, scans count:', scans.length);
  const { t } = useI18n();
  const [hoveredNode, setHoveredNode] = useState(null);
  const [hoveredFlow, setHoveredFlow] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 1200, height: 500 });

  // LLM Model selection
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [showModelSelector, setShowModelSelector] = useState(false);

  // CVE data from backend
  const [cveData, setCveData] = useState([]);
  const [cveLoading, setCveLoading] = useState(false);

  // Poll for analysis status when generating
  useEffect(() => {
    if (!analysisData || analysisData.status !== 'generating') return;

    const analysisId = analysisData.id;
    if (!analysisId) return;

    let stopped = false;
    const poll = async () => {
      while (!stopped) {
        await new Promise(r => setTimeout(r, 8000));
        if (stopped) break;
        try {
          const token = localStorage.getItem('access_token') || localStorage.getItem('token');
          if (!token) break;
          const resp = await fetch(`${API_BASE}/api/v1/sankey-analysis/${analysisId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!resp.ok) break;
          const data = await resp.json();
          if (stopped) break;
          setAnalysisData(data);
          if (data.status === 'completed' || data.status === 'failed') break;
        } catch (e) {
          console.warn('[Sankey] Poll error:', e);
          break;
        }
      }
    };
    poll();
    return () => { stopped = true; };
  }, [analysisData?.id, analysisData?.status]);

  // Fetch CVEs from backend - with retry logic
  useEffect(() => {
    let isMounted = true;

    const fetchCVEs = async (retryCount = 0) => {
      try {
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        if (!token) {
          console.log('[Sankey] No token found, skipping CVE fetch');
          return;
        }

        // Get workspace ID from scans or from any available workspace
        let workspaceId = scans[0]?.workspaces?.[0]?.id || scans[0]?.workspace_id;

        // If no workspace from scans, try to find one from other scans
        if (!workspaceId && scans.length > 0) {
          for (const scan of scans) {
            if (scan.workspace_id) {
              workspaceId = scan.workspace_id;
              break;
            }
            if (scan.workspaces && scan.workspaces.length > 0) {
              workspaceId = scan.workspaces[0].id;
              break;
            }
          }
        }

        const url = workspaceId
          ? `${API_BASE}/api/v1/sankey-analysis/cves/for-sankey?workspace_id=${workspaceId}`
          : `${API_BASE}/api/v1/sankey-analysis/cves/for-sankey`;

        console.log('[Sankey] Fetching CVEs from:', url, 'scans:', scans.length);
        setCveLoading(true);

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('[Sankey] CVE response status:', response.status);

        if (response.ok) {
          const data = await response.json();
          console.log('[Sankey] CVE data received:', data?.cves?.length || 0, 'CVEs');
          if (isMounted && data.success && data.cves) {
            setCveData(data.cves);
          } else if (isMounted && data.cves) {
            // Handle case where success might be missing but cves exist
            setCveData(data.cves);
          }
        } else if (response.status === 401) {
          console.error('[Sankey] CVE fetch unauthorized - token might be expired');
        } else {
          console.error('[Sankey] CVE fetch failed:', response.status, response.statusText);
          // Retry once if failed and scans exist
          if (retryCount < 1 && scans.length > 0) {
            setTimeout(() => fetchCVEs(retryCount + 1), 1000);
          }
        }
      } catch (err) {
        // Use console.warn for network errors (expected in some conditions) to avoid polluting error logs
        if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
          console.warn('[Sankey] Network error fetching CVEs, will retry if possible');
        } else {
          console.warn('[Sankey] Error fetching CVEs:', err.message);
        }
        // Retry on network error
        if (retryCount < 1 && scans.length > 0) {
          setTimeout(() => fetchCVEs(retryCount + 1), 1000);
        }
      } finally {
        if (isMounted) {
          setCveLoading(false);
        }
      }
    };

    // Fetch CVEs immediately and when scans change
    fetchCVEs();

    return () => {
      isMounted = false;
    };
  }, [scans]);

  // Fetch available LLM models
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`${API_BASE}/api/v1/sankey-analysis/models/available`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.models) {
            setAvailableModels(data.models);
            // Default model
            if (!selectedModel && data.default) {
              setSelectedModel(data.default);
            }
          }
        }
      } catch (err) {
        // Use console.warn for network errors to avoid polluting error logs
        if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
          console.warn('[Sankey] Network error fetching models, may retry later');
        } else {
          console.warn('[Sankey] Error fetching models:', err.message);
        }
      }
    };
    fetchModels();
  }, []);

  // Helper to get workspace info from scan (scan.workspaces is an array)
  const getWorkspaceFromScan = (scan) => {
    // scan.workspaces is an array like [{id, name, company_name}]
    if (scan.workspaces && scan.workspaces.length > 0) {
      const ws = scan.workspaces[0];
      return {
        id: ws.id,
        name: ws.name || ws.company_name || 'Workspace'
      };
    }
    // Fallback to workspace_id if present
    if (scan.workspace_id) {
      return {
        id: scan.workspace_id,
        name: scan.workspace_name || 'Workspace'
      };
    }
    return {
      id: 'no-workspace',
      name: 'Unassigned'
    };
  };

  // Fetch AI analysis for a clicked element
  const fetchAnalysis = useCallback(async (elementType, elementId, elementName, workspaceId = null, scanId = null) => {
    setAnalysisLoading(true);
    setAnalysisError(null);

    const requestBody = {
      element_type: elementType,
      element_id: elementId,
      element_name: elementName,
      workspace_id: workspaceId,
      scan_id: scanId,
      regenerate: false
    };

    console.log('[Sankey] Fetching analysis:', requestBody);

    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      if (!token) {
        throw new Error('Session expired. Please reconnect.');
      }

      const response = await fetch(`${API_BASE}/api/v1/sankey-analysis/analyze`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      console.log('[Sankey] Analysis response status:', response.status);

      // Handle 401 Unauthorized specifically
      if (response.status === 401) {
        throw new Error('Session expired. Please reconnect.');
      }

      const data = await response.json();
      console.log('[Sankey] Analysis response data:', {
        status: data.status,
        hasDetailedAnalysis: !!data.detailed_analysis,
        hasSummary: !!data.summary,
        errorMessage: data.error_message
      });

      if (!response.ok) {
        throw new Error(data.detail || 'Error retrieving analysis');
      }

      setAnalysisData(data);
    } catch (err) {
      console.error('[Sankey] Analysis fetch error:', err);
      setAnalysisError(err.message);
    } finally {
      setAnalysisLoading(false);
    }
  }, []);

  // Regenerate analysis with optional model parameter
  const regenerateAnalysis = useCallback(async (modelOverride = null) => {
    if (!selectedNode) return;

    setAnalysisLoading(true);
    setAnalysisError(null);
    setShowModelSelector(false);

    const modelToUse = modelOverride || selectedModel;

    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      if (!token) {
        throw new Error('Session expired. Please reconnect.');
      }

      const response = await fetch(`${API_BASE}/api/v1/sankey-analysis/analyze`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          element_type: selectedNode.elementType,
          element_id: selectedNode.elementId,
          element_name: selectedNode.name,
          workspace_id: selectedNode.workspaceId || null,
          scan_id: selectedNode.scanId || null,
          regenerate: true,
          model: modelToUse
        })
      });

      // Handle 401 Unauthorized specifically
      if (response.status === 401) {
        throw new Error('Session expired. Please reconnect.');
      }

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Error regenerating analysis');
      }
      setAnalysisData(data);
    } catch (err) {
      console.error('Analysis regenerate error:', err);
      setAnalysisError(err.message);
    } finally {
      setAnalysisLoading(false);
    }
  }, [selectedNode, selectedModel]);

  // Handle node click
  const handleNodeClick = useCallback((node, elementType) => {
    const elementId = node.id?.toString() || '';
    const workspaceId = elementType === 'workspace' ? node.id : (node.workspace_id || null);
    const scanId = node.scans?.[0]?.id || null;

    console.log('[Sankey] Node clicked:', {
      elementType,
      elementId,
      workspaceId,
      nodeName: node.name,
      scanId,
      nodeData: node
    });

    setSelectedNode({
      ...node,
      elementType,
      elementId,
      workspaceId,
      scanId
    });

    fetchAnalysis(elementType, elementId, node.name, workspaceId, scanId);
  }, [fetchAnalysis]);

  // Close analysis panel
  const closeAnalysisPanel = useCallback(() => {
    setSelectedNode(null);
    setAnalysisData(null);
    setAnalysisError(null);
  }, []);

  // Resize handler
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: Math.max(rect.width - 40, 800),
          height: Math.max(rect.height - 40, 400)
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Build Sankey data structure - using scan data directly (no API calls needed)
  const sankeyData = useMemo(() => {
    if (!scans || scans.length === 0) {
      return { nodes: [], flows: [], levels: [] };
    }

    // Level 1: Workspaces - aggregate data from scans
    const workspaceMap = new Map();
    scans.forEach(scan => {
      const wsInfo = getWorkspaceFromScan(scan);
      if (!workspaceMap.has(wsInfo.id)) {
        workspaceMap.set(wsInfo.id, {
          id: wsInfo.id,
          name: wsInfo.name,
          scans: [],
          hosts: 0,
          vulns: 0,
          critical: 0,
          high: 0,
          medium: 0,
          low: 0
        });
      }
      const ws = workspaceMap.get(wsInfo.id);
      ws.scans.push(scan);
      ws.hosts += scan.total_hosts_found || scan.hosts_up || 0;
      ws.vulns += scan.total_vulnerabilities || 0;
      ws.critical += scan.critical_count || 0;
      ws.high += scan.high_count || 0;
      ws.medium += scan.medium_count || 0;
      ws.low += scan.low_count || 0;
    });

    // Level 2: Targets (unique per workspace)
    const targetMap = new Map();
    scans.forEach(scan => {
      const wsInfo = getWorkspaceFromScan(scan);
      const targets = scan.targets || [scan.target];
      targets.forEach(target => {
        if (!target) return;
        const key = `${wsInfo.id}-${target}`;
        if (!targetMap.has(key)) {
          targetMap.set(key, {
            id: key,
            name: target,
            workspace_id: wsInfo.id,
            hosts: scan.total_hosts_found || scan.hosts_up || 0,
            ports: scan.total_ports || 0,
            vulns: scan.total_vulnerabilities || 0,
            critical: scan.critical_count || 0,
            high: scan.high_count || 0,
            medium: scan.medium_count || 0,
            low: scan.low_count || 0
          });
        } else {
          // Aggregate if same target appears multiple times
          const t = targetMap.get(key);
          t.hosts += scan.total_hosts_found || scan.hosts_up || 0;
          t.ports += scan.total_ports || 0;
          t.vulns += scan.total_vulnerabilities || 0;
          t.critical += scan.critical_count || 0;
          t.high += scan.high_count || 0;
          t.medium += scan.medium_count || 0;
          t.low += scan.low_count || 0;
        }
      });
    });

    // Level 3: Assets - create from scans (using target as asset representation)
    // Since we don't have individual host data, we'll create one asset per scan target
    const assetMap = new Map();
    scans.forEach(scan => {
      const target = scan.target;
      if (!target) return;
      const wsInfo = getWorkspaceFromScan(scan);
      const key = `${scan.id}-${target}`;

      if (!assetMap.has(key)) {
        assetMap.set(key, {
          id: key,
          name: scan.name || target,
          ip: target,
          workspace_id: wsInfo.id,
          scan_id: scan.id,
          target: target,
          hostCount: scan.total_hosts_found || scan.hosts_up || 0,
          vulns: {
            critical: scan.critical_count || 0,
            high: scan.high_count || 0,
            medium: scan.medium_count || 0,
            low: scan.low_count || 0,
            unresolved: scan.total_vulnerabilities || 0
          }
        });
      }
    });

    // Level 4: Vulnerabilities aggregated from scans
    let totalCritical = 0, totalHigh = 0, totalMedium = 0, totalLow = 0;
    scans.forEach(scan => {
      totalCritical += scan.critical_count || 0;
      totalHigh += scan.high_count || 0;
      totalMedium += scan.medium_count || 0;
      totalLow += scan.low_count || 0;
    });

    const totalVulns = totalCritical + totalHigh + totalMedium + totalLow;

    return {
      workspaces: Array.from(workspaceMap.values()),
      targets: Array.from(targetMap.values()),
      assets: Array.from(assetMap.values()),
      vulnerabilities: {
        critical: totalCritical,
        high: totalHigh,
        medium: totalMedium,
        low: totalLow,
        total: totalVulns
      },
      resolution: {
        resolved: 0,
        unresolved: totalVulns
      }
    };
  }, [scans]);

  // Calculate positions for Sankey diagram
  const layout = useMemo(() => {
    const { width, height } = dimensions;
    const padding = { top: 60, bottom: 40, left: 30, right: 30 };
    const levelWidth = (width - padding.left - padding.right) / 6;
    const nodeWidth = 20;
    // Increased minimum node height to accommodate multi-line labels
    const minNodeHeight = 40;
    // Maximum label width to prevent overlap with next column
    const maxLabelWidth = levelWidth - nodeWidth - 50;

    // Level positions - 6 columns including CVE
    const levels = [
      { x: padding.left, label: t('sankey.columns.workspaces', 'Workspaces') },
      { x: padding.left + levelWidth, label: t('sankey.columns.targets', 'Cibles') },
      { x: padding.left + levelWidth * 2, label: t('sankey.columns.assets', 'Assets') },
      { x: padding.left + levelWidth * 3, label: t('sankey.columns.vulnerabilities', 'Vulnerabilities') },
      { x: padding.left + levelWidth * 4, label: t('sankey.columns.exploitability', 'Exploitability') },
      { x: padding.left + levelWidth * 5, label: t('sankey.columns.cve', 'CVE') }
    ];

    const availableHeight = height - padding.top - padding.bottom;

    // Calculate node heights based on value
    const calculateNodes = (items, getValue) => {
      if (items.length === 0) return [];

      // The label for each node needs at least this much vertical space
      // (2 lines of text + padding). We must ensure node height + gap >= this.
      const minLabelHeight = 36;

      // Gap between nodes - must be large enough that label backgrounds don't touch
      const nodeGap = Math.max(8, items.length > 10 ? 10 : items.length > 5 ? 12 : 14);
      // Minimum node height must accommodate the label
      const effectiveMinHeight = Math.max(minLabelHeight, items.length > 10 ? 28 : items.length > 8 ? 32 : minNodeHeight);

      const total = items.reduce((sum, item) => sum + Math.max(1, getValue(item)), 0);

      // Calculate total required height and scale if needed to fit
      const totalGapsHeight = (items.length - 1) * nodeGap;
      const totalMinContentHeight = items.length * effectiveMinHeight;
      const requiredHeight = totalGapsHeight + totalMinContentHeight;

      // If content doesn't fit, scale down proportionally
      const scaleFactor = requiredHeight > availableHeight ? availableHeight / requiredHeight : 1;
      const adjustedMinHeight = effectiveMinHeight * scaleFactor;
      const adjustedGap = nodeGap * scaleFactor;

      const heightPerUnit = (availableHeight - (items.length - 1) * adjustedGap) / Math.max(total, 1);

      let currentY = padding.top;
      return items.map((item, i) => {
        const value = Math.max(1, getValue(item));
        const nodeHeight = Math.max(adjustedMinHeight, value * heightPerUnit * scaleFactor);
        const node = {
          ...item,
          y: currentY,
          height: nodeHeight,
          value,
          // Store item count for font size adjustment
          totalItems: items.length
        };
        currentY += nodeHeight + adjustedGap;
        return node;
      });
    };

    // Position nodes at each level
    const workspaceNodes = calculateNodes(
      sankeyData.workspaces || [],
      ws => ws.scans.length + (ws.hosts || 0)
    );

    const targetNodes = calculateNodes(
      (sankeyData.targets || []).slice(0, 8), // Limit to 8 targets for visibility
      t => 1
    );

    const assetNodes = calculateNodes(
      (sankeyData.assets || []).slice(0, 10), // Limit to 10 assets for visibility
      a => a.vulns.unresolved + 1
    );

    // Vulnerability severity nodes
    const vulnData = sankeyData.vulnerabilities || { critical: 0, high: 0, medium: 0, low: 0 };
    const vulnItems = [
      { id: 'critical', name: t('sankey.severity.critical', 'Critiques'), count: vulnData.critical, color: '#ef4444' },
      { id: 'high', name: t('sankey.severity.high', 'Hautes'), count: vulnData.high, color: '#f97316' },
      { id: 'medium', name: t('sankey.severity.medium', 'Moyennes'), count: vulnData.medium, color: '#eab308' },
      { id: 'low', name: t('sankey.severity.low', 'Faibles'), count: vulnData.low, color: '#22c55e' }
    ].filter(v => v.count > 0);

    const vulnNodes = calculateNodes(vulnItems, v => v.count);

    // Exploitability nodes (based on critical/high as exploitable)
    const resolution = sankeyData.resolution || { resolved: 0, unresolved: 0 };
    const exploitableCount = vulnData.critical + vulnData.high;
    const nonExploitableCount = vulnData.medium + vulnData.low;
    const exploitabilityItems = [
      { id: 'exploitable', name: t('sankey.exploitable', 'Exploitable'), count: exploitableCount, color: '#ef4444' },
      { id: 'non-exploitable', name: t('sankey.nonExploitable', 'Non Exploitable'), count: nonExploitableCount, color: '#22c55e' }
    ].filter(r => r.count > 0);

    const resolutionNodes = calculateNodes(exploitabilityItems, r => r.count);

    // CVE nodes - use real CVE data from backend or empty
    const cveItems = cveData && cveData.length > 0
      ? cveData.slice(0, 8).map(cve => ({
          id: cve.cve_id,
          name: cve.cve_id,
          title: cve.title,
          count: cve.count,
          color: cve.color,
          severity: cve.severity,
          cvss_score: cve.cvss_score,
          isCve: true
        }))
      : [];

    const cveNodes = calculateNodes(cveItems, c => c.count || 1);

    return {
      levels,
      workspaceNodes,
      targetNodes,
      assetNodes,
      vulnNodes,
      resolutionNodes,
      cveNodes,
      nodeWidth,
      padding,
      maxLabelWidth
    };
  }, [sankeyData, dimensions, cveData, t]);

  // Generate flow paths between nodes
  const generateFlowPath = (sourceX, sourceY, sourceHeight, targetX, targetY, targetHeight, flowRatio = 1) => {
    const sourceMidY = sourceY + sourceHeight / 2;
    const targetMidY = targetY + targetHeight / 2;
    const sourceEndX = sourceX + layout.nodeWidth;
    const controlOffset = (targetX - sourceEndX) / 2;

    const flowHeight = Math.min(sourceHeight, targetHeight) * flowRatio;
    const sourceTop = sourceMidY - flowHeight / 2;
    const sourceBottom = sourceMidY + flowHeight / 2;
    const targetTop = targetMidY - flowHeight / 2;
    const targetBottom = targetMidY + flowHeight / 2;

    return `
      M ${sourceEndX} ${sourceTop}
      C ${sourceEndX + controlOffset} ${sourceTop},
        ${targetX - controlOffset} ${targetTop},
        ${targetX} ${targetTop}
      L ${targetX} ${targetBottom}
      C ${targetX - controlOffset} ${targetBottom},
        ${sourceEndX + controlOffset} ${sourceBottom},
        ${sourceEndX} ${sourceBottom}
      Z
    `;
  };

  // Get color based on severity
  const getNodeColor = (node, level) => {
    if (level === 'workspace') return '#00D4AA';
    if (level === 'target') return '#0ea5e9';
    if (level === 'asset') {
      if (node.vulns?.critical > 0) return '#ef4444';
      if (node.vulns?.high > 0) return '#f97316';
      if (node.vulns?.medium > 0) return '#eab308';
      return '#22c55e';
    }
    return node.color || '#00D4AA';
  };

  // Truncate text to fit within available width (approximate based on character count)
  // Average character width varies by font size - being more conservative to prevent overflow
  const truncateText = useCallback((text, maxWidth, fontSize = 12) => {
    if (!text) return '';
    // Even more conservative character width estimates to ensure text never overflows
    const avgCharWidth = fontSize <= 9 ? 5.8 : fontSize === 10 ? 6.2 : fontSize === 11 ? 6.8 : 7.2;
    const maxChars = Math.floor(maxWidth / avgCharWidth);
    if (text.length <= maxChars) return text;
    return text.substring(0, Math.max(maxChars - 3, 1)) + '...';
  }, []);

  // Get dynamic font size based on number of items in column
  const getDynamicFontSize = useCallback((baseSize, totalItems) => {
    if (totalItems > 12) return Math.max(baseSize - 4, 8);
    if (totalItems > 10) return Math.max(baseSize - 3, 8);
    if (totalItems > 8) return Math.max(baseSize - 2, 9);
    if (totalItems > 5) return Math.max(baseSize - 1, 10);
    return baseSize;
  }, []);

  // Get risk level color
  const getRiskColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'critical': return 'text-red-500 bg-red-500/10 border-red-500/30';
      case 'high': return 'text-orange-500 bg-orange-500/10 border-orange-500/30';
      case 'medium': return 'text-yellow-500 bg-yellow-500/10 border-yellow-500/30';
      case 'low': return 'text-green-500 bg-green-500/10 border-green-500/30';
      default: return 'text-gray-500 bg-gray-500/10 border-gray-500/30';
    }
  };

  if (!scans || scans.length === 0) {
    return (
      <div className="bg-brand-black-light rounded-xl border border-brand-green/30 p-8">
        <div className="flex flex-col items-center justify-center h-[300px] text-gray-500">
          <Target className="w-16 h-16 mb-4 opacity-30" />
          <p>{t('sankey.noScansAvailable', 'Aucun scan disponible pour la visualisation')}</p>
        </div>
      </div>
    );
  }

  const { levels, workspaceNodes, targetNodes, assetNodes, vulnNodes, resolutionNodes, cveNodes, nodeWidth, padding, maxLabelWidth } = layout;

  // Get valid workspace ID for analysis requests (must be UUID, not 'no-workspace')
  const validWorkspaceId = sankeyData.workspaces?.find(ws => isValidUUID(ws.id))?.id || null;

  return (
    <div className="space-y-4">
      {/* Sankey Diagram */}
      <div className="bg-brand-black-light rounded-xl border border-brand-green/30 overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-800 flex items-center justify-between">
          <h2 className="text-xl font-bold text-brand-green flex items-center gap-2">
            <Target className="w-5 h-5" />
            {t('sankey.title', 'Security Flow - Flow Diagram')}
          </h2>
          <div className="flex items-center gap-4 text-sm text-gray-400 overflow-hidden min-w-0">
            <div className="flex items-center gap-2 min-w-0 flex-shrink">
              <Brain className="w-4 h-4 text-brand-green flex-shrink-0" />
              <span className="truncate">{t('sankey.clickForAiAnalysis', 'Click on an element for AI analysis')}</span>
            </div>
            <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
              <div className="w-3 h-3 rounded bg-brand-green flex-shrink-0"></div>
              <span className="truncate">{t('sankey.legend.workspaces', 'Workspaces')}</span>
            </div>
            <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
              <div className="w-3 h-3 rounded bg-sky-500 flex-shrink-0"></div>
              <span className="truncate">{t('sankey.legend.targets', 'Cibles')}</span>
            </div>
            <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
              <div className="w-3 h-3 rounded bg-red-500 flex-shrink-0"></div>
              <span className="truncate">{t('sankey.legend.critical', 'Critique')}</span>
            </div>
            <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
              <div className="w-3 h-3 rounded bg-green-500 flex-shrink-0"></div>
              <span className="truncate">{t('sankey.legend.nonExploitable', 'Non Exploit.')}</span>
            </div>
          </div>
        </div>

        {/* Sankey Diagram */}
        <div ref={containerRef} className="relative h-[500px] overflow-hidden flex justify-center">
          <svg width={dimensions.width} height={dimensions.height} className="max-w-full h-full">
            <defs>
              {/* Gradients for flows */}
              <linearGradient id="flow-workspace-target" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#00D4AA" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.4" />
              </linearGradient>
              <linearGradient id="flow-target-asset" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#f97316" stopOpacity="0.4" />
              </linearGradient>
              <linearGradient id="flow-asset-vuln" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#f97316" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#ef4444" stopOpacity="0.4" />
              </linearGradient>
              <linearGradient id="flow-vuln-exploitable" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#ef4444" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#ef4444" stopOpacity="0.4" />
              </linearGradient>
              <linearGradient id="flow-vuln-non-exploitable" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#eab308" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#22c55e" stopOpacity="0.4" />
              </linearGradient>
              <linearGradient id="flow-exploitable-cve" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#ef4444" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#a855f7" stopOpacity="0.4" />
              </linearGradient>
              <linearGradient id="flow-non-exploitable-cve" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#22c55e" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#a855f7" stopOpacity="0.4" />
              </linearGradient>

              {/* Glow filter */}
              <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Level Labels */}
            {levels.map((level, i) => (
              <text
                key={i}
                x={level.x + nodeWidth / 2}
                y={padding.top - 25}
                textAnchor="middle"
                className="fill-gray-400 text-xs font-medium"
              >
                {level.label}
              </text>
            ))}

            {/* Flows: Workspaces -> Targets */}
            {workspaceNodes.map((ws, wsIdx) => (
              targetNodes
                .filter(t => t.workspace_id === ws.id)
                .map((target, tIdx) => (
                  <path
                    key={`flow-ws-${wsIdx}-t-${tIdx}`}
                    d={generateFlowPath(
                      levels[0].x, ws.y, ws.height,
                      levels[1].x, target.y, target.height,
                      0.8
                    )}
                    fill="url(#flow-workspace-target)"
                    className={`transition-opacity duration-200 ${
                      hoveredFlow === `ws-${ws.id}` ? 'opacity-100' : 'opacity-60'
                    }`}
                    onMouseEnter={() => setHoveredFlow(`ws-${ws.id}`)}
                    onMouseLeave={() => setHoveredFlow(null)}
                  />
                ))
            ))}

            {/* Flows: Targets -> Assets (simplified - connect all) */}
            {targetNodes.length > 0 && assetNodes.length > 0 && (
              <path
                d={generateFlowPath(
                  levels[1].x,
                  targetNodes[0]?.y || padding.top,
                  targetNodes.reduce((sum, t) => sum + t.height, 0) + (targetNodes.length - 1) * 8,
                  levels[2].x,
                  assetNodes[0]?.y || padding.top,
                  assetNodes.reduce((sum, a) => sum + a.height, 0) + (assetNodes.length - 1) * 8,
                  1
                )}
                fill="url(#flow-target-asset)"
                className="opacity-50"
              />
            )}

            {/* Flows: Assets -> Vulnerabilities */}
            {assetNodes.length > 0 && vulnNodes.length > 0 && (
              <path
                d={generateFlowPath(
                  levels[2].x,
                  assetNodes[0]?.y || padding.top,
                  assetNodes.reduce((sum, a) => sum + a.height, 0) + (assetNodes.length - 1) * 8,
                  levels[3].x,
                  vulnNodes[0]?.y || padding.top,
                  vulnNodes.reduce((sum, v) => sum + v.height, 0) + (vulnNodes.length - 1) * 8,
                  1
                )}
                fill="url(#flow-asset-vuln)"
                className="opacity-50"
              />
            )}

            {/* Flows: Vulnerabilities -> Resolution */}
            {vulnNodes.length > 0 && resolutionNodes.length > 0 && resolutionNodes.map((res, resIdx) => (
              <path
                key={`flow-vuln-res-${resIdx}`}
                d={generateFlowPath(
                  levels[3].x,
                  vulnNodes[0]?.y || padding.top,
                  vulnNodes.reduce((sum, v) => sum + v.height, 0) + (vulnNodes.length - 1) * 8,
                  levels[4].x,
                  res.y,
                  res.height,
                  res.count / (sankeyData.vulnerabilities?.total || 1)
                )}
                fill={res.id === 'exploitable' ? 'url(#flow-vuln-exploitable)' : 'url(#flow-vuln-non-exploitable)'}
                className="opacity-50"
              />
            ))}

            {/* Flows: Resolution -> CVE */}
            {resolutionNodes.length > 0 && cveNodes.length > 0 && (
              <path
                d={generateFlowPath(
                  levels[4].x,
                  resolutionNodes[0]?.y || padding.top,
                  resolutionNodes.reduce((sum, r) => sum + r.height, 0) + (resolutionNodes.length - 1) * 8,
                  levels[5].x,
                  cveNodes[0]?.y || padding.top,
                  cveNodes.reduce((sum, c) => sum + c.height, 0) + (cveNodes.length - 1) * 8,
                  1
                )}
                fill="url(#flow-exploitable-cve)"
                className="opacity-50"
              />
            )}

            {/* Workspace Nodes */}
            {workspaceNodes.map((ws, i) => {
              const fontSize = getDynamicFontSize(12, ws.totalItems || workspaceNodes.length);
              const subFontSize = getDynamicFontSize(10, ws.totalItems || workspaceNodes.length);
              const lineSpacing = fontSize > 10 ? 14 : 12;
              const labelX = levels[0].x + nodeWidth + 8;
              const labelY = ws.y + ws.height / 2;
              // Constrain label bg to not exceed node vertical bounds
              const labelBgHeight = Math.min(lineSpacing * 2 + 8, ws.height);
              const showSubLabel = ws.height >= lineSpacing * 2 + 4;
              return (
                <g
                  key={`ws-${i}`}
                  className="cursor-pointer"
                  onClick={() => handleNodeClick(ws, 'workspace')}
                  onMouseEnter={() => { setHoveredNode(ws); setHoveredFlow(`ws-${ws.id}`); }}
                  onMouseLeave={() => { setHoveredNode(null); setHoveredFlow(null); }}
                >
                  <rect
                    x={levels[0].x}
                    y={ws.y}
                    width={nodeWidth}
                    height={ws.height}
                    rx={4}
                    fill="#00D4AA"
                    filter={hoveredNode?.id === ws.id || selectedNode?.id === ws.id ? 'url(#glow)' : undefined}
                    className="transition-all duration-200"
                    stroke={selectedNode?.id === ws.id ? '#00D4AA' : 'none'}
                    strokeWidth={selectedNode?.id === ws.id ? 2 : 0}
                  />
                  {/* Label background for readability over flows */}
                  <rect
                    x={labelX - 4}
                    y={labelY - labelBgHeight / 2}
                    width={maxLabelWidth + 8}
                    height={labelBgHeight}
                    rx={3}
                    fill="rgba(17, 17, 17, 0.85)"
                  />
                  <text
                    x={labelX}
                    y={showSubLabel ? labelY - (lineSpacing / 4) : labelY}
                    dominantBaseline="middle"
                    className="fill-white font-medium"
                    style={{ fontSize: `${fontSize}px` }}
                  >
                    {truncateText(ws.name || 'Workspace', maxLabelWidth, fontSize)}
                  </text>
                  {showSubLabel && (
                    <text
                      x={labelX}
                      y={labelY + lineSpacing}
                      dominantBaseline="middle"
                      className="fill-gray-500"
                      style={{ fontSize: `${subFontSize}px` }}
                    >
                      {truncateText(`${ws.scans.length} ${t('sankey.scansLabel', 'scans')} • ${ws.hosts} ${t('sankey.hostsLabel', 'hosts')}`, maxLabelWidth, subFontSize)}
                    </text>
                  )}
                </g>
              );
            })}

            {/* Target Nodes */}
            {targetNodes.map((target, i) => {
              const fontSize = getDynamicFontSize(12, target.totalItems || targetNodes.length);
              const subFontSize = getDynamicFontSize(10, target.totalItems || targetNodes.length);
              const lineSpacing = fontSize > 10 ? 12 : 10;
              const labelX = levels[1].x + nodeWidth + 8;
              const labelY = target.y + target.height / 2;
              const labelBgHeight = Math.min(lineSpacing * 2 + 8, target.height);
              const showSubLabel = target.height >= lineSpacing * 2 + 4;
              return (
                <g
                  key={`target-${i}`}
                  className="cursor-pointer"
                  onClick={() => handleNodeClick(target, 'asset')}
                  onMouseEnter={() => setHoveredNode(target)}
                  onMouseLeave={() => setHoveredNode(null)}
                >
                  <rect
                    x={levels[1].x}
                    y={target.y}
                    width={nodeWidth}
                    height={target.height}
                    rx={4}
                    fill="#0ea5e9"
                    filter={hoveredNode?.id === target.id || selectedNode?.id === target.id ? 'url(#glow)' : undefined}
                    className="transition-all duration-200"
                    stroke={selectedNode?.id === target.id ? '#0ea5e9' : 'none'}
                    strokeWidth={selectedNode?.id === target.id ? 2 : 0}
                  />
                  {/* Label background for readability over flows */}
                  <rect
                    x={labelX - 4}
                    y={labelY - labelBgHeight / 2}
                    width={maxLabelWidth + 8}
                    height={labelBgHeight}
                    rx={3}
                    fill="rgba(17, 17, 17, 0.85)"
                  />
                  <text
                    x={labelX}
                    y={showSubLabel ? labelY - (lineSpacing / 3) : labelY}
                    dominantBaseline="middle"
                    className="fill-white font-medium"
                    style={{ fontSize: `${fontSize}px` }}
                  >
                    {truncateText(target.name || 'Cible', maxLabelWidth, fontSize)}
                  </text>
                  {showSubLabel && (
                    <text
                      x={labelX}
                      y={labelY + lineSpacing}
                      dominantBaseline="middle"
                      className="fill-gray-400"
                      style={{ fontSize: `${subFontSize}px` }}
                    >
                      {truncateText(target.ports > 0 ? `${target.ports} ${t('sankey.portsLabel', 'ports')}` : target.hosts > 0 ? `${target.hosts} ${t('sankey.hostsLabel', 'hosts')}` : '', maxLabelWidth, subFontSize)}
                    </text>
                  )}
                </g>
              );
            })}

            {/* Asset Nodes */}
            {assetNodes.map((asset, i) => {
              const fontSize = getDynamicFontSize(12, asset.totalItems || assetNodes.length);
              const subFontSize = getDynamicFontSize(9, asset.totalItems || assetNodes.length);
              const lineSpacing = fontSize > 10 ? 12 : 10;
              const labelX = levels[2].x + nodeWidth + 8;
              const labelY = asset.y + asset.height / 2;
              const labelBgHeight = Math.min(lineSpacing * 2 + 8, asset.height);
              const showSubLabel = asset.height >= lineSpacing * 2 + 4;
              return (
                <g
                  key={`asset-${i}`}
                  className="cursor-pointer"
                  onClick={() => handleNodeClick(asset, 'asset')}
                  onMouseEnter={() => setHoveredNode(asset)}
                  onMouseLeave={() => setHoveredNode(null)}
                >
                  <rect
                    x={levels[2].x}
                    y={asset.y}
                    width={nodeWidth}
                    height={asset.height}
                    rx={4}
                    fill={getNodeColor(asset, 'asset')}
                    filter={hoveredNode?.id === asset.id || selectedNode?.id === asset.id ? 'url(#glow)' : undefined}
                    className="transition-all duration-200"
                    stroke={selectedNode?.id === asset.id ? getNodeColor(asset, 'asset') : 'none'}
                    strokeWidth={selectedNode?.id === asset.id ? 2 : 0}
                  />
                  {/* Label background for readability over flows */}
                  <rect
                    x={labelX - 4}
                    y={labelY - labelBgHeight / 2}
                    width={maxLabelWidth + 8}
                    height={labelBgHeight}
                    rx={3}
                    fill="rgba(17, 17, 17, 0.85)"
                  />
                  {/* IP Address */}
                  <text
                    x={labelX}
                    y={showSubLabel ? labelY - (lineSpacing / 3) : labelY}
                    dominantBaseline="middle"
                    className="fill-white font-medium"
                    style={{ fontSize: `${fontSize}px` }}
                  >
                    {truncateText(asset.ip || asset.name, maxLabelWidth, fontSize)}
                  </text>
                  {/* Vulnerability breakdown */}
                  {showSubLabel && asset.vulns && (asset.vulns.critical > 0 || asset.vulns.high > 0 || asset.vulns.medium > 0 || asset.vulns.low > 0) && (
                    <text
                      x={labelX}
                      y={labelY + lineSpacing}
                      dominantBaseline="middle"
                      style={{ fontSize: `${subFontSize}px` }}
                    >
                      {asset.vulns.critical > 0 && (
                        <tspan fill="#ef4444">{asset.vulns.critical}C </tspan>
                      )}
                      {asset.vulns.high > 0 && (
                        <tspan fill="#f97316">{asset.vulns.high}H </tspan>
                      )}
                      {asset.vulns.medium > 0 && (
                        <tspan fill="#eab308">{asset.vulns.medium}M </tspan>
                      )}
                      {asset.vulns.low > 0 && (
                        <tspan fill="#22c55e">{asset.vulns.low}L</tspan>
                      )}
                    </text>
                  )}
                </g>
              );
            })}

            {/* Vulnerability Nodes */}
            {vulnNodes.map((vuln, i) => {
              const fontSize = getDynamicFontSize(12, vuln.totalItems || vulnNodes.length);
              const subFontSize = getDynamicFontSize(10, vuln.totalItems || vulnNodes.length);
              const lineSpacing = fontSize > 10 ? 12 : 10;
              const labelX = levels[3].x + nodeWidth + 8;
              const labelY = vuln.y + vuln.height / 2;
              return (
                <g
                  key={`vuln-${i}`}
                  className="cursor-pointer"
                  onClick={() => handleNodeClick({ ...vuln, id: vuln.id, workspace_id: validWorkspaceId }, 'vulnerability')}
                  onMouseEnter={() => setHoveredNode(vuln)}
                  onMouseLeave={() => setHoveredNode(null)}
                >
                  <rect
                    x={levels[3].x}
                    y={vuln.y}
                    width={nodeWidth}
                    height={vuln.height}
                    rx={4}
                    fill={vuln.color}
                    filter={hoveredNode?.id === vuln.id || selectedNode?.id === vuln.id ? 'url(#glow)' : undefined}
                    className="transition-all duration-200"
                    stroke={selectedNode?.id === vuln.id ? vuln.color : 'none'}
                    strokeWidth={selectedNode?.id === vuln.id ? 2 : 0}
                  />
                  {/* Label background for readability over flows */}
                  <rect
                    x={labelX - 4}
                    y={labelY - lineSpacing - 2}
                    width={maxLabelWidth + 8}
                    height={lineSpacing * 2 + 8}
                    rx={3}
                    fill="rgba(17, 17, 17, 0.85)"
                  />
                  <text
                    x={labelX}
                    y={labelY - (lineSpacing / 4)}
                    dominantBaseline="middle"
                    className="fill-white font-medium"
                    style={{ fontSize: `${fontSize}px` }}
                  >
                    {truncateText(vuln.name, maxLabelWidth, fontSize)}
                  </text>
                  <text
                    x={labelX}
                    y={labelY + lineSpacing}
                    dominantBaseline="middle"
                    style={{ fill: vuln.color, fontSize: `${subFontSize}px` }}
                    className="font-bold"
                  >
                    {vuln.count}
                  </text>
                </g>
              );
            })}

            {/* Resolution Nodes */}
            {resolutionNodes.map((res, i) => {
              const fontSize = getDynamicFontSize(12, res.totalItems || resolutionNodes.length);
              const subFontSize = getDynamicFontSize(10, res.totalItems || resolutionNodes.length);
              const lineSpacing = fontSize > 10 ? 12 : 10;
              const labelX = levels[4].x + nodeWidth + 8;
              const labelY = res.y + res.height / 2;
              return (
                <g
                  key={`res-${i}`}
                  className="cursor-pointer"
                  onClick={() => handleNodeClick({ ...res, id: res.id, workspace_id: validWorkspaceId }, 'exploitability')}
                  onMouseEnter={() => setHoveredNode(res)}
                  onMouseLeave={() => setHoveredNode(null)}
                >
                  <rect
                    x={levels[4].x}
                    y={res.y}
                    width={nodeWidth}
                    height={res.height}
                    rx={4}
                    fill={res.color}
                    filter={hoveredNode?.id === res.id || selectedNode?.id === res.id ? 'url(#glow)' : undefined}
                    className="transition-all duration-200"
                    stroke={selectedNode?.id === res.id ? res.color : 'none'}
                    strokeWidth={selectedNode?.id === res.id ? 2 : 0}
                  />
                  {/* Label background for readability over flows */}
                  <rect
                    x={labelX - 4}
                    y={labelY - lineSpacing - 2}
                    width={maxLabelWidth + 8}
                    height={lineSpacing * 2 + 8}
                    rx={3}
                    fill="rgba(17, 17, 17, 0.85)"
                  />
                  <text
                    x={labelX}
                    y={labelY - (lineSpacing / 4)}
                    dominantBaseline="middle"
                    className="fill-white font-medium"
                    style={{ fontSize: `${fontSize}px` }}
                  >
                    {truncateText(res.name, maxLabelWidth, fontSize)}
                  </text>
                  <text
                    x={labelX}
                    y={labelY + lineSpacing}
                    dominantBaseline="middle"
                    style={{ fill: res.color, fontSize: `${subFontSize}px` }}
                    className="font-bold"
                  >
                    {res.count}
                  </text>
                </g>
              );
            })}

            {/* CVE Nodes */}
            {cveNodes.map((cve, i) => {
              const fontSize = getDynamicFontSize(10, cve.totalItems || cveNodes.length);
              const subFontSize = getDynamicFontSize(9, cve.totalItems || cveNodes.length);
              const lineSpacing = fontSize > 9 ? 10 : 8;
              const labelX = levels[5].x + nodeWidth + 8;
              const labelY = cve.y + cve.height / 2;
              return (
                <g
                  key={`cve-${i}`}
                  className="cursor-pointer"
                  onClick={() => handleNodeClick({ ...cve, id: cve.id, workspace_id: validWorkspaceId }, 'cve')}
                  onMouseEnter={() => setHoveredNode(cve)}
                  onMouseLeave={() => setHoveredNode(null)}
                >
                  <rect
                    x={levels[5].x}
                    y={cve.y}
                    width={nodeWidth}
                    height={cve.height}
                    rx={4}
                    fill={cve.color || '#a855f7'}
                    filter={hoveredNode?.id === cve.id || selectedNode?.id === cve.id ? 'url(#glow)' : undefined}
                    className="transition-all duration-200"
                    stroke={selectedNode?.id === cve.id ? (cve.color || '#a855f7') : 'none'}
                    strokeWidth={selectedNode?.id === cve.id ? 2 : 0}
                  />
                  {/* Label background for readability over flows */}
                  <rect
                    x={labelX - 4}
                    y={labelY - lineSpacing - 2}
                    width={maxLabelWidth + 8}
                    height={lineSpacing * 2 + 8}
                    rx={3}
                    fill="rgba(17, 17, 17, 0.85)"
                  />
                  {/* CVE ID */}
                  <text
                    x={labelX}
                    y={labelY - (lineSpacing / 3)}
                    dominantBaseline="middle"
                    className="fill-white font-medium"
                    style={{ fontSize: `${fontSize}px` }}
                  >
                    {truncateText(cve.name, maxLabelWidth, fontSize)}
                  </text>
                  {/* CVE count */}
                  <text
                    x={labelX}
                    y={labelY + lineSpacing}
                    dominantBaseline="middle"
                    style={{ fill: cve.color || '#a855f7', fontSize: `${subFontSize}px` }}
                    className="font-bold"
                  >
                    {truncateText(`${cve.count} ${t('sankey.occurrences', 'occurrence(s)')}`, maxLabelWidth, subFontSize)}
                  </text>
                </g>
              );
            })}

            {/* Flow direction arrows */}
            {levels.slice(0, -1).map((level, i) => (
              <g key={`arrow-${i}`} opacity={0.3}>
                <ChevronRight
                  x={level.x + (levels[i + 1].x - level.x) / 2 - 8}
                  y={dimensions.height / 2 - 8}
                  className="w-4 h-4 text-gray-600"
                />
              </g>
            ))}
          </svg>

          {/* Tooltip */}
          {hoveredNode && !selectedNode && (
            <div
              className="absolute bg-brand-black border border-brand-green/50 rounded-lg p-3 shadow-xl pointer-events-none z-50"
              style={{
                left: '50%',
                bottom: '20px',
                transform: 'translateX(-50%)'
              }}
            >
              <div className="text-white font-medium mb-1">{hoveredNode.name || t('sankey.tooltip.element', 'Element')}</div>
              {hoveredNode.scans && (
                <div className="text-xs text-gray-400">
                  {hoveredNode.scans.length} {t('sankey.scansLabel', 'scans')} • {hoveredNode.hosts || 0} {t('sankey.hostsLabel', 'hosts')} • {hoveredNode.vulns || 0} {t('sankey.stats.vulnerabilities', 'vulnerabilities')}
                </div>
              )}
              {hoveredNode.ip && (
                <div className="text-xs text-gray-400">
                  {hoveredNode.ip} • {hoveredNode.hostCount || 0} {t('sankey.hostsLabel', 'hosts')} • {hoveredNode.vulns?.unresolved || 0} {t('sankey.stats.vulnerabilities', 'vulnerabilities')}
                </div>
              )}
              {hoveredNode.count !== undefined && (
                <div className="text-xs" style={{ color: hoveredNode.color }}>
                  {hoveredNode.count} {t('sankey.stats.vulnerabilities', 'vulnerabilities')}
                </div>
              )}
              <div className="text-xs text-brand-green mt-2 flex items-center gap-1">
                <Brain className="w-3 h-3" /> {t('sankey.tooltip.clickForAi', "Cliquez pour l'analyse IA")}
              </div>
            </div>
          )}
        </div>

        {/* Summary Stats */}
        <div className="p-4 border-t border-gray-800 grid grid-cols-5 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-brand-green">{sankeyData.workspaces?.length || 0}</div>
            <div className="text-xs text-gray-500">{t('sankey.stats.workspaces', 'Espaces clients')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-sky-500">{sankeyData.targets?.length || 0}</div>
            <div className="text-xs text-gray-500">{t('sankey.stats.targets', 'Cibles')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-500">{sankeyData.assets?.length || 0}</div>
            <div className="text-xs text-gray-500">{t('sankey.stats.assets', 'Assets')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-500">{sankeyData.vulnerabilities?.total || 0}</div>
            <div className="text-xs text-gray-500">{t('sankey.stats.vulnerabilities', 'Vulnerabilities')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-500">
              {sankeyData.vulnerabilities?.total ?
                Math.round(((sankeyData.vulnerabilities?.critical || 0) + (sankeyData.vulnerabilities?.high || 0)) / sankeyData.vulnerabilities.total * 100) : 0}%
            </div>
            <div className="text-xs text-gray-500">{t('sankey.stats.exploitable', 'Exploitable')}</div>
          </div>
        </div>
      </div>

      {/* AI Analysis Panel - Below Sankey */}
      {selectedNode && (
        <div className="w-full bg-gradient-to-br from-brand-black-light to-brand-black rounded-xl border border-brand-green/40 overflow-hidden shadow-xl animate-fade-in">
          {/* Panel Header */}
          <div className="p-4 border-b border-gray-800 flex items-center justify-between bg-brand-black/50">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-brand-green/20">
                <Brain className="w-5 h-5 text-brand-green" />
              </div>
              <div>
                <h3 className="font-bold text-white flex items-center gap-2">
                  Analyse IA - {selectedNode.name}
                  {analysisData?.risk_level && (
                    <span className={`px-2 py-0.5 text-xs rounded-full border ${getRiskColor(analysisData.risk_level)}`}>
                      {analysisData.risk_level.toUpperCase()}
                    </span>
                  )}
                </h3>
                <p className="text-xs text-gray-500">
                  Type: {selectedNode.elementType} • {analysisData?.generated_at ? `Generated on ${new Date(analysisData.generated_at).toLocaleString()}` : 'Generating...'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Model info */}
              {analysisData?.model_used && (
                <span className="text-xs text-gray-500 px-2 py-1 bg-gray-800 rounded-lg">
                  {t('sankey.analysis.model', 'Model')}: {analysisData.model_used}
                </span>
              )}

              {/* Model selector dropdown */}
              {analysisData && (
                <div className="relative">
                  <button
                    onClick={() => setShowModelSelector(!showModelSelector)}
                    disabled={analysisLoading}
                    className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-brand-green transition-colors disabled:opacity-50 flex items-center gap-1"
                    title={t('sankey.analysis.changeModel', 'Analyser avec un autre LLM')}
                  >
                    <Brain className="w-4 h-4" />
                    <ChevronRight className={`w-3 h-3 transition-transform ${showModelSelector ? 'rotate-90' : ''}`} />
                  </button>

                  {/* Dropdown */}
                  {showModelSelector && (
                    <div className="absolute right-0 top-full mt-2 w-72 bg-brand-black border border-gray-700 rounded-lg shadow-xl z-50 overflow-hidden">
                      <div className="p-2 border-b border-gray-700 bg-gray-900/50">
                        <p className="text-sm font-medium text-white">{t('sankey.analysis.selectModel', 'Select an LLM model')}</p>
                        <p className="text-xs text-gray-500">{t('sankey.analysis.regenerateWith', 'Regenerate analysis with another model')}</p>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {availableModels.map((model) => (
                          <button
                            key={model.id}
                            onClick={() => {
                              setSelectedModel(model.id);
                              regenerateAnalysis(model.id);
                            }}
                            className={`w-full px-3 py-2 text-left hover:bg-gray-800 transition-colors border-b border-gray-800/50 last:border-0 ${
                              model.id === analysisData?.model_used ? 'bg-brand-green/10' : ''
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-sm font-medium text-white flex items-center gap-2">
                                  {model.name}
                                  {model.recommended && (
                                    <span className="text-xs px-1.5 py-0.5 bg-brand-green/20 text-brand-green rounded">
                                      {t('sankey.analysis.recommended', 'Recommended')}
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500">{model.description}</div>
                              </div>
                              <span className="text-xs text-gray-600">{model.size}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {analysisData && (
                <button
                  onClick={() => regenerateAnalysis()}
                  disabled={analysisLoading}
                  className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-brand-green transition-colors disabled:opacity-50"
                  title={t('sankey.analysis.regenerate', 'Regenerate analysis')}
                >
                  <RefreshCw className={`w-4 h-4 ${analysisLoading ? 'animate-spin' : ''}`} />
                </button>
              )}
              <button
                onClick={closeAnalysisPanel}
                className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Panel Content */}
          <div className="p-4">
            {analysisLoading && (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-10 h-10 text-brand-green animate-spin mb-4" />
                <p className="text-gray-400">Generating AI analysis...</p>
                <p className="text-xs text-gray-600 mt-2">Cela peut prendre quelques secondes</p>
              </div>
            )}

            {analysisError && !analysisLoading && (
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 text-center">
                <Brain className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                <p className="text-gray-300 font-medium mb-2">
                  {analysisError.includes('Session expired') ? 'Session expired' : 'Analysis error'}
                </p>
                <p className="text-gray-500 text-sm mb-4">
                  {analysisError}
                </p>
                <button
                  onClick={() => fetchAnalysis(selectedNode.elementType, selectedNode.elementId, selectedNode.name, selectedNode.workspaceId, selectedNode.scanId)}
                  className="px-4 py-2 bg-brand-green/20 hover:bg-brand-green/30 text-brand-green rounded-lg text-sm transition-colors flex items-center gap-2 mx-auto"
                >
                  <RefreshCw className="w-4 h-4" />
                  {analysisError.includes('Session expired') ? 'Reconnect' : 'Retry'}
                </button>
              </div>
            )}

            {/* Generating — show spinner while polling */}
            {analysisData && !analysisLoading && analysisData.status === 'generating' && (
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 text-center">
                <Loader2 className="w-10 h-10 text-brand-green mx-auto mb-3 animate-spin" />
                <p className="text-gray-300 font-medium mb-2">Analyse IA en cours…</p>
                <p className="text-gray-500 text-sm">The model is generating the analysis. Auto-update in a few moments.</p>
              </div>
            )}

            {/* Handle failed analysis */}
            {analysisData && !analysisLoading && analysisData.status === 'failed' && (
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 text-center">
                <Brain className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                <p className="text-gray-300 font-medium mb-2">Generation failed</p>
                <p className="text-gray-500 text-sm mb-4">
                  {analysisData.error_message || 'The AI analysis could not be generated. Please try again.'}
                </p>
                <button
                  onClick={() => regenerateAnalysis()}
                  className="px-4 py-2 bg-brand-green/20 hover:bg-brand-green/30 text-brand-green rounded-lg text-sm transition-colors flex items-center gap-2 mx-auto"
                >
                  <RefreshCw className="w-4 h-4" />
                  Regenerate analysis
                </button>
              </div>
            )}

            {/* No content (non-failed, non-generating) */}
            {analysisData && !analysisLoading && analysisData.status !== 'failed' && analysisData.status !== 'generating' && !analysisData.detailed_analysis && !analysisData.summary && (
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 text-center">
                <Brain className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                <p className="text-gray-300 font-medium mb-2">Analyse vide</p>
                <button
                  onClick={() => regenerateAnalysis()}
                  className="px-4 py-2 bg-brand-green/20 hover:bg-brand-green/30 text-brand-green rounded-lg text-sm transition-colors flex items-center gap-2 mx-auto"
                >
                  <RefreshCw className="w-4 h-4" />
                  Regenerate analysis
                </button>
              </div>
            )}

            {analysisData && !analysisLoading && analysisData.status !== 'failed' && (analysisData.detailed_analysis || analysisData.summary) && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Left Column - Summary & Risk */}
                <div className="space-y-4">
                  {/* Risk Score */}
                  {analysisData.risk_score !== null && (
                    <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                      <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Score de Risque
                      </h4>
                      <div className="flex items-center gap-4">
                        <div className="relative w-20 h-20">
                          <svg className="w-20 h-20 transform -rotate-90">
                            <circle cx="40" cy="40" r="35" stroke="#374151" strokeWidth="6" fill="none" />
                            <circle
                              cx="40" cy="40" r="35"
                              stroke={analysisData.risk_score >= 75 ? '#ef4444' : analysisData.risk_score >= 50 ? '#f97316' : analysisData.risk_score >= 25 ? '#eab308' : '#22c55e'}
                              strokeWidth="6"
                              fill="none"
                              strokeDasharray={`${analysisData.risk_score * 2.2} 220`}
                              strokeLinecap="round"
                            />
                          </svg>
                          <span className="absolute inset-0 flex items-center justify-center text-2xl font-bold text-white">
                            {analysisData.risk_score}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className={`text-lg font-bold ${getRiskColor(analysisData.risk_level).split(' ')[0]}`}>
                            {analysisData.risk_level?.toUpperCase()}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">On a scale from 0 to 100</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Summary */}
                  {analysisData.summary && (
                    <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                      <h4 className="text-sm font-medium text-gray-400 mb-2 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Summary
                      </h4>
                      <MarkdownRenderer content={analysisData.summary} variant="compact" />
                    </div>
                  )}

                  {/* Key Findings */}
                  {analysisData.key_findings && analysisData.key_findings.length > 0 && (
                    <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                      <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                        <Lightbulb className="w-4 h-4" />
                        Key Findings
                      </h4>
                      <div className="space-y-2">
                        {analysisData.key_findings.slice(0, 5).map((finding, idx) => (
                          <div key={idx} className={`p-2 rounded-lg border ${
                            finding.type === 'critical' ? 'bg-red-500/10 border-red-500/30' :
                            finding.type === 'warning' ? 'bg-orange-500/10 border-orange-500/30' :
                            'bg-blue-500/10 border-blue-500/30'
                          }`}>
                            <div className="font-medium text-sm text-white">{finding.title}</div>
                            {finding.description && (
                              <div className="text-xs text-gray-400 mt-1">{finding.description}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Center Column - Detailed Analysis */}
                <div className="lg:col-span-2 space-y-4">
                  {/* Detailed Analysis */}
                  {analysisData.detailed_analysis && (
                    <div className="bg-brand-black rounded-lg p-4 border border-gray-800 max-h-[400px] overflow-y-auto">
                      <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                        <Brain className="w-4 h-4" />
                        Detailed Analysis
                      </h4>
                      <div className="text-sm text-gray-200 leading-relaxed space-y-3 [&>h1]:text-xl [&>h1]:font-bold [&>h1]:text-white [&>h1]:mt-4 [&>h1]:mb-2 [&>h2]:text-lg [&>h2]:font-semibold [&>h2]:text-white [&>h2]:mt-3 [&>h2]:mb-2 [&>h3]:text-base [&>h3]:font-semibold [&>h3]:text-gray-100 [&>h3]:mt-2 [&>h3]:mb-1 [&>p]:text-gray-300 [&>p]:mb-2 [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:text-gray-300 [&>ol]:list-decimal [&>ol]:pl-5 [&>ol]:text-gray-300 [&>li]:mb-1 [&>strong]:text-white [&>strong]:font-semibold [&>code]:bg-gray-800 [&>code]:px-1.5 [&>code]:py-0.5 [&>code]:rounded [&>code]:text-brand-green [&>blockquote]:border-l-4 [&>blockquote]:border-brand-green [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:text-gray-400">
                        <ReactMarkdown>{analysisData.detailed_analysis}</ReactMarkdown>
                      </div>
                    </div>
                  )}

                  {/* Attack Vectors */}
                  {analysisData.attack_vectors && analysisData.attack_vectors.length > 0 && (
                    <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                      <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                        <Crosshair className="w-4 h-4" />
                        Vecteurs d'Attaque
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {analysisData.attack_vectors.map((vector, idx) => (
                          <div key={idx} className="p-3 rounded-lg bg-gray-900/50 border border-gray-800">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm text-white">{vector.name}</span>
                              <div className="flex items-center gap-2">
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  vector.likelihood === 'high' ? 'bg-red-500/20 text-red-400' :
                                  vector.likelihood === 'medium' ? 'bg-orange-500/20 text-orange-400' :
                                  'bg-green-500/20 text-green-400'
                                }`}>
                                  {vector.likelihood}
                                </span>
                              </div>
                            </div>
                            <p className="text-xs text-gray-500">{vector.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recommendations */}
                  {analysisData.recommendations && analysisData.recommendations.length > 0 && (
                    <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                      <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                        <ArrowUpRight className="w-4 h-4" />
                        Recommandations
                      </h4>
                      <div className="space-y-2">
                        {analysisData.recommendations.map((rec, idx) => (
                          <div key={idx} className="p-3 rounded-lg bg-gradient-to-r from-brand-green/5 to-transparent border border-brand-green/20">
                            <div className="flex items-start gap-3">
                              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-green/20 text-brand-green text-xs font-bold flex items-center justify-center">
                                {rec.priority || idx + 1}
                              </span>
                              <div className="flex-1">
                                <div className="font-medium text-sm text-white">{rec.title}</div>
                                {rec.description && (
                                  <p className="text-xs text-gray-400 mt-1">{rec.description}</p>
                                )}
                                {rec.effort && (
                                  <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded ${
                                    rec.effort === 'low' ? 'bg-green-500/20 text-green-400' :
                                    rec.effort === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                                    'bg-red-500/20 text-red-400'
                                  }`}>
                                    Effort: {rec.effort}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SankeyWorkspaceFlow;
